import Vue from "vue";
import Router from "vue-router";
import has from "lodash.has";
import Editor from "@/views/Editor";
import Campaigns from "@/views/Campaigns";
import Upgrade from "@/views/Upgrade";
import Templates from "@/views/Templates";
import Template from "@/views/Template";
import Unsubscribe from "@/views/Unsubscribe";
import Auth from "@/views/auth/Auth";
import Plans from "@/views/Plans";
import Downgrade from "@/views/Downgrade";
import TestSend from "@/views/TestSend";
import FourOhFour from "@/views/FourOhFour";
import TemplateListing from "@/components/Templates/TemplateListing";
import TemplateCategory from "@/components/Templates/TemplateCategory";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import EditorLayout from "@/layouts/EditorLayout.vue";
import { isAuthenticated } from "@/lib/auth";
import { getUrlParameter } from "@/utils/utils";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    // Home
    {
      path: "/",
      name: "Home",
      redirect: () => "/campaigns",
      meta: { requiresAuth: true },
    },
    // Campaigns
    {
      path: "/campaigns",
      name: "Campaigns",
      component: Campaigns,
      meta: { requiresAuth: true, layout: DefaultLayout },
    },
    // Campaign/:id
    {
      path: "/campaign/:id",
      name: "Campaign",
      meta: { requiresAuth: true, layout: EditorLayout },
      component: Editor,
      children: [
        {
          path: "edit",
          meta: { requiresAuth: true, layout: EditorLayout },
          name: "CampaignEditMode",
          redirect: () => "/campaign/:id",
        },
      ],
    },
    // Campaign (redirect)
    {
      path: "/campaign",
      meta: { requiresAuth: true },
      redirect: (to) => {
        // if there is an upgradeSuccessful query string and it's value is false...
        if (
          has(to, "query.upgradeSuccessful") &&
          to.query.upgradeSuccessful === "true"
        ) {
          // Get the most recent campaignId from local storage
          const campaignId = window.localStorage.getItem(
            "pai-currentCampaignId"
          );

          // Redirect the user to that campaign
          if (campaignId) return `/campaign/${campaignId}`;

          // If no campaignId is found then show an error on the dashboard by just returning to the main route below
          // The querystring should be automatically appended
        }

        // return redirect path/location
        return "/campaigns";
      },
    },
    // Upgrade
    {
      path: "/upgrade",
      name: "Upgrade",
      component: Upgrade,
      meta: { requiresAuth: true },
    },
    // Templates
    {
      path: "/templates",
      component: Templates,
      meta: { requiresAuth: true, layout: DefaultLayout },
      children: [
        {
          path: "",
          component: TemplateListing,
          name: "Templates",
          meta: { requiresAuth: true, layout: DefaultLayout },
        },
        {
          path: ":category",
          meta: { requiresAuth: true, layout: DefaultLayout },
          component: TemplateCategory,
          name: "TemplateCategory",
        },
      ],
    },
    // Template
    {
      path: "/templates/:category/:id",
      name: "Template",
      component: Template,
      meta: { requiresAuth: true, layout: DefaultLayout },
    },
    // Login
    {
      path: "/login",
      meta: { requiresAuth: false },
      name: "Login",
      component: Auth,
      beforeEnter: (to, from, next) => {
        saveQueryParams("offer", "pai-offerCode");
        saveQueryParams("redirect", "pai-redirect", to?.query?.redirect);
        next();
      },
    },
    // Unsubscribed
    {
      path: "/unsubscribed",
      meta: { requiresAuth: false },
      name: "Unsubscribe",
      component: Unsubscribe,
    },
    // Plans
    {
      path: "/plans",
      name: "Plans",
      component: Plans,
      meta: { requiresAuth: true },
    },
    // TestSend
    {
      path: "/testsend",
      name: "TestSend",
      component: TestSend,
      meta: { requiresAuth: false },
    },
    // Downgrade
    {
      path: "/downgrade",
      name: "Downgrade",
      component: Downgrade,
      meta: { requiresAuth: true },
    },
    {
      // will match everything
      path: '*',
      name: "FourOhFour",
      component: FourOhFour,
      meta: { requiresAuth: false },
    }
  ],
});

// Setup Auth checks
router.beforeEach(async (to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    // this route requires auth, check if logged in
    // if not, redirect to login page.
    if (!isAuthenticated()) {
      next({
        path: "/login",
        query: { redirect: to.fullPath },
      });
    } else {
      next();
    }
  } else {
    next(); // make sure to always call next()!
  }
});

function saveQueryParams(param, key, data) {
  let value = data || getUrlParameter(param);

  if (value) window.localStorage.setItem(key, value);
}

export default router;
